import React, { useState } from "react";

import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import KeyboardArrowUpIcon from "@mui/icons-material/KeyboardArrowUp";
import { Divider, IconButton, Tooltip } from "@mui/material";
import { NeutralColor, SemanticColor } from "node-library/dist/colors";
import { Severity } from "node-library/dist/constants";
import { getFileDisplayName } from "node-library/dist/helpers/file_helper";
import { areStringArraysEqual } from "node-library/dist/helpers/misc_helper";
import {
	hasPermission,
	PermissionMap,
	Permissions,
} from "node-library/dist/permissions";
import {
	FrontendCompanySampleMatrix,
	FrontendCompanyTestCategory,
	FrontendCompanyTestExpectationField,
	FrontendCompanyTestHierarchyNode,
	FrontendCompanyTestPackage,
	FrontendCompanyTestResultField,
	FrontendSampleAdditionalInfoMapping,
	FrontendSampleInstance,
	FrontendSampleInstanceAdditionalInfoMapping,
	FrontendSampleTestInformation,
	FrontendUserInfo,
	FrontendCompanyWithAdditionalInfomation,
	TestHierarchyTreeNode,
} from "node-library/dist/portal";
import { useTranslation } from "react-i18next";
import { KebabDropdownButton } from "../../../common-components/button";
import { CollapsibleCard } from "../../../common-components/card";
import {
	AlertIcon,
	CopyIcon,
	DeleteIcon,
	EditIcon,
	InfoIcon,
	PaperClipIcon,
} from "../../../common-components/svgIcons";
import { RedErrorIcon } from "../../../common-components/toast/errorIcons";
import { useSnackbar } from "../../../common-components/toast/showToastMessage";
import { GreenTickIcon } from "../../../common-components/toast/successIcons";
import { YellowWarnIcon } from "../../../common-components/toast/warnIcons";
import { downloadSpecificationFile } from "../../../helpers/frontendFileOperationHelper";
import {
	getAdditionalInfoValueDisplayText,
	getShortTurnaroundTime,
	getTurnaroundTimeBackgroundColor,
} from "../../../helpers/sampleHelper";
import { renderEnabledTestInfoSection } from "../../../helpers/testHierarchyHelper";

interface Props {
	company: FrontendCompanyWithAdditionalInfomation;
	user: FrontendUserInfo;
	isImportMode?: boolean;
	orderTestCategory: FrontendCompanyTestCategory;
	hierarchyNodeParentMap: Map<string, FrontendCompanyTestHierarchyNode[]>;
	instanceHierarchyTree: TestHierarchyTreeNode;

	sampleInstance: FrontendSampleInstance;

	index: number;
	deleteSample: () => void;
	goToUpdateSample: () => void;
	isReadOnlyTable?: boolean;
	isDetailsPageTable?: boolean;
	duplicateSample: () => void;
	openEditExpectationModal: () => void;
	openSpecReadingDrawer: () => void;
}

export default function SampleCard(props: Props) {
	const { t: translation } = useTranslation();
	const { openSnackbar } = useSnackbar();

	const [expanded, setExpanded] = useState(false);

	const {
		company,
		sampleInstance,
		deleteSample,
		goToUpdateSample,
		duplicateSample,
		isImportMode,
		orderTestCategory,
		hierarchyNodeParentMap,
		instanceHierarchyTree,
		openEditExpectationModal,
		openSpecReadingDrawer,
	} = props;

	const testInfo: FrontendSampleTestInformation =
		sampleInstance.sampleTestInfo as FrontendSampleTestInformation;

	const selectedPackage: FrontendCompanyTestPackage | undefined =
		orderTestCategory.testPackages.find((pack) => {
			return pack.packageId === sampleInstance.packageId;
		});

	const selectedNodeIds: Array<string> = testInfo.selectedTests.map((test) => {
		return test.selectedNodeId;
	});
	const packageTestNodeIds = selectedPackage?.packageTestNodeIds || [];
	const packageCustomized: boolean =
		packageTestNodeIds.length == 0
			? false
			: !areStringArraysEqual(packageTestNodeIds, selectedNodeIds);
	const selectedMatrix: FrontendCompanySampleMatrix =
		props.orderTestCategory.sampleMatrices.find((matrix) => {
			return matrix.matrixId === sampleInstance.sample.matrixId;
		}) as FrontendCompanySampleMatrix;
	const sampleAdditionalInfos: Array<FrontendSampleAdditionalInfoMapping> =
		sampleInstance.sample.sampleAdditionalInfos;
	const sampleInstanceAdditionalInfos: Array<FrontendSampleInstanceAdditionalInfoMapping> =
		sampleInstance.sampleInstanceAdditionalInfos;
	async function handleDownloadSpecification() {
		try {
			if (!sampleInstance.specification) {
				return;
			}
			if (
				sampleInstance.specification.isNewSpecification &&
				sampleInstance.specification.canDownload
			) {
				const result: boolean = await downloadSpecificationFile(
					sampleInstance.specification,
				);
				if (!result) {
					openSnackbar(
						translation("FailedToDownloadSpecification"),
						Severity.ERROR,
						translation("SystemErrorMsg"),
						<RedErrorIcon />,
					);
				}
			} else {
				openSpecReadingDrawer();
			}
		} catch (error: any) {
			console.error(error);
			openSnackbar(
				translation("SystemError"),
				Severity.ERROR,
				translation("SystemErrorMsg"),
				<RedErrorIcon />,
			);
		}
	}

	const doesUserHaveCreateOrderAccess: boolean = props.user
		? hasPermission(
				props.user?.role?.permissions as PermissionMap,
				Permissions.orders.create,
			)
		: false;
	const doesUserHaveEditOrderAccess: boolean = props.user
		? hasPermission(
				props.user?.role?.permissions as PermissionMap,
				Permissions.orders.updateStatus,
			)
		: false;
	const doesUserHaveUpdateOrderStatusAccess: boolean = props.user
		? hasPermission(
				props.user?.role?.permissions as PermissionMap,
				Permissions.orders.updateStatus,
			)
		: false;

	const actionMenuOptions =
		!doesUserHaveCreateOrderAccess &&
		!doesUserHaveEditOrderAccess &&
		doesUserHaveUpdateOrderStatusAccess
			? [
					{
						key: `edit-expectation-${props.index}`,
						onClick: () => {
							openEditExpectationModal(); // Or a separate function if needed
						},
						children: (
							<div className="icon-dropdown-menu-item">
								<EditIcon strokeColor={NeutralColor.NEUTRAL_GREY_950} />
								{translation("EditExpectation")}
							</div>
						),
					},
				]
			: [
					{
						key: `edit-batch-${props.index}`,
						onClick: () => {
							goToUpdateSample();
						},
						children: (
							<div className="icon-dropdown-menu-item">
								<EditIcon strokeColor={NeutralColor.NEUTRAL_GREY_950} />
								{translation("Edit")}
							</div>
						),
					},
					{
						key: `duplicate-batch-${props.index}`,
						onClick: () => {
							duplicateSample();
						},
						children: (
							<div className="icon-dropdown-menu-item">
								<CopyIcon />
								{translation("Duplicate")}
							</div>
						),
					},
					{
						key: `delete-order-${props.index}`,
						onClick: () => {
							deleteSample();
						},
						children: (
							<div className="icon-dropdown-menu-item">
								<DeleteIcon />
								{translation("Delete")}
							</div>
						),
					},
				];

	const sampleCardContent: React.ReactNode = (
		<div
			className={`sample-preview-list-row ${
				props.isReadOnlyTable ? "read-only" : ""
			} ${props.isDetailsPageTable ? "order-details" : ""}`}
		>
			<div className=" sample-preview-number-column">
				{props.isDetailsPageTable
					? sampleInstance.sample.fullSampleId || ""
					: String(props.index + 1).padStart(2, "0")}
			</div>
			<div className="sample-preview-info-column sample-preview-basic-info">
				<div className="typography-body-01 overflow-ellipsis-text">
					{sampleInstance.sample.sampleName}
				</div>
				<div className="typography-body-02 overflow-ellipsis-text">{`${translation(
					"Lot",
				)}: ${sampleInstance.sample.lotCode}`}</div>
			</div>
			<div className="sample-preview-test-info">
				<div className="sample-preview-info-column overflow-ellipsis-text">
					<Divider
						className="sample-preview-collapsible-divider"
						orientation="horizontal"
						flexItem
					/>
					<div className="typography-body-01 overflow-ellipsis-text ">
						{`${selectedPackage?.packageName || translation("Customize")}${
							packageCustomized ? "(" + translation("Customized") + ")" : ""
						}`}
					</div>
					<div style={{ display: "flex", gap: "10px", alignItems: "center" }}>
						<div style={{ display: "flex", gap: "5px" }}>
							<div
								className="typography-body-02 turnaround-time-box"
								style={{
									backgroundColor: getTurnaroundTimeBackgroundColor(
										(
											sampleInstance.sampleTestInfo as FrontendSampleTestInformation
										).turnaroundTime,
									),
								}}
							>
								{getShortTurnaroundTime(
									(
										sampleInstance.sampleTestInfo as FrontendSampleTestInformation
									).turnaroundTime,
									translation,
								)}
							</div>
						</div>
						{sampleInstance.specification ? (
							<>
								<Divider orientation="vertical" flexItem />{" "}
								<div style={{ display: "flex", alignItems: "center" }}>
									{sampleInstance.specification.active ? (
										sampleInstance.specification.isNewSpecification ? (
											<Tooltip
												PopperProps={{
													sx: {
														"& .MuiTooltip-tooltip": {
															backgroundColor: NeutralColor.NEUTRAL_GREY_950,
															padding: "12px 16px",
														},
													},
												}}
												title={`${translation(
													"NewSpecificationFromClientPortal",
												)}`}
												arrow
											>
												<span style={{ display: "flex", width: "fit-content" }}>
													<InfoIcon
														strokeColor={SemanticColor.INFO_MEDIUM}
													></InfoIcon>
												</span>
											</Tooltip>
										) : (
											<></>
										)
									) : (
										<Tooltip
											PopperProps={{
												sx: {
													"& .MuiTooltip-tooltip": {
														backgroundColor: NeutralColor.NEUTRAL_GREY_950,
														padding: "12px 16px",
													},
												},
											}}
											title={`${translation("SpecificationDeletedError")}`}
											arrow
										>
											<span style={{ display: "flex", width: "fit-content" }}>
												<AlertIcon></AlertIcon>
											</span>
										</Tooltip>
									)}
									<PaperClipIcon />
									<div
										className="typography-body-02 link-like-text"
										onClick={() => {
											handleDownloadSpecification();
										}}
									>
										{getFileDisplayName(
											sampleInstance.specification.fileName,
											sampleInstance.specification.format,
										)}
									</div>
								</div>
							</>
						) : (
							<></>
						)}
					</div>
				</div>
				{isImportMode && (
					<div
						style={{
							display: "flex",
							alignItems: "center",
							gap: "5px",
							color: sampleInstance.pendingReview
								? SemanticColor.WARNING_300
								: SemanticColor.SUCCESS_300,
							cursor: "pointer",
						}}
						onClick={goToUpdateSample}
					>
						{sampleInstance.pendingReview ? (
							<>
								<YellowWarnIcon></YellowWarnIcon>
								{translation("PendingReview")}
							</>
						) : (
							<>
								<GreenTickIcon></GreenTickIcon>
								{translation("InstanceReviewed")}
							</>
						)}
					</div>
				)}
			</div>
			{!props.isReadOnlyTable ? (
				<div className="sample-preview-button-column sample-preview-more-action-column">
					<KebabDropdownButton
						menuOptions={actionMenuOptions}
						id={`batch-more-action-menu-${props.index}`}
						onClick={(e) => {}}
					></KebabDropdownButton>
				</div>
			) : (
				<></>
			)}
			<div className="sample-preview-button-column sample-preview-expand-button">
				<IconButton
					onClick={() => {
						setExpanded(!expanded);
					}}
				>
					{expanded ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}
				</IconButton>
			</div>
		</div>
	);

	const expectationFieldIdToNameMap = new Map<string, string>();
	for (const field of company.testResultAndExpectationFields) {
		const expectationField: FrontendCompanyTestExpectationField | undefined =
			field.expectationField;
		if (!expectationField) {
			continue;
		}
		const fieldId: string = expectationField.expectationFieldId;
		if (field) {
			expectationFieldIdToNameMap.set(fieldId, field.fieldName);
		}
	}

	const fieldsHavingExpectations: FrontendCompanyTestResultField[] =
		company.testResultAndExpectationFields.filter((resultField) => {
			return resultField.expectationField != undefined;
		});

	const testInfoNode: Array<React.ReactNode> = renderEnabledTestInfoSection(
		instanceHierarchyTree,
		"typography-body-02",
		fieldsHavingExpectations,
		testInfo.expectationInputs,
		expectationFieldIdToNameMap,
		company.testResultUnits,
		company.hierarchyNodeMappingPackages,
	);

	const TATBackgroundColor: string = getTurnaroundTimeBackgroundColor(
		testInfo.turnaroundTime,
	);
	const testInfoList: React.ReactNode = (
		<div key={testInfo.turnaroundTime} className="sample-preview-info-column">
			<div style={{ display: "flex" }}>
				<div
					className="typography-body-02 turnaround-time-box"
					style={{ backgroundColor: TATBackgroundColor }}
				>
					{translation(testInfo.turnaroundTime)}
				</div>
			</div>

			{testInfoNode}

			{testInfo.additionalTest && (
				<div className="view-sample-info-text-row typography-body-02">
					<div className="sample-info-row-description">{`${translation(
						"Additional",
					)}: `}</div>
					<div className="wrap-text ">{testInfo.additionalTestDescription}</div>
				</div>
			)}
		</div>
	);

	const collapsibleContent: React.ReactNode = (
		<>
			<div
				className={`sample-preview-list-row sample-preview-collapsible-content ${
					props.isReadOnlyTable ? "read-only" : ""
				} ${props.isDetailsPageTable ? "order-details" : ""}`}
			>
				<div className=" sample-preview-number-column">{/* Placeholder*/}</div>
				<div className="sample-preview-info-column sample-preview-expanded-basic-info-column">
					<div className="typography-body-02 typography-bold view-sample-info-image-row">
						<div className="sample-information-icon sample-details-icon"></div>
						<div>{translation("SampleDetails")}</div>
					</div>
					<div className="typography-body-02 view-sample-info-text-row">
						{`${translation("Matrix")}: `}
						<span className="view-sample-info-info-text">
							{sampleInstance.sample.isCustomMatrix
								? sampleInstance.sample.matrix
								: `${selectedMatrix.subMatrix}`}
						</span>
					</div>
					<div className="typography-body-02 view-sample-info-text-row">
						{`${translation("SampleSize")}: `}
						<span className="view-sample-info-info-text">
							{`${sampleInstance.weight} ${sampleInstance.weightUnit}`}
						</span>
					</div>
					<div className="typography-body-02 view-sample-info-text-row">
						{`${translation("SampleDescription")}: `}
						<span className="view-sample-info-info-text wrap-text">
							{sampleInstance.sample.sampleDescription}
						</span>
					</div>
					<div className="typography-body-02 view-sample-info-text-row">
						{`${translation("Retestsample")}: `}
						<span className="view-sample-info-info-text">
							{sampleInstance.isRetest ? translation("Yes") : translation("No")}
						</span>
					</div>
					<div className="typography-body-02 view-sample-info-text-row">
						{`${translation("Additionaltest")}: `}
						<span className="view-sample-info-info-text">
							{sampleInstance.isAdditionalTest
								? translation("Yes")
								: translation("No")}
						</span>
					</div>
					<div className="typography-body-02 view-sample-info-text-row">
						{`${translation("Treatment")}: `}
						<span className="view-sample-info-info-text">
							{sampleInstance.treatment
								? translation("Yes")
								: translation("No")}
						</span>
					</div>
					{sampleAdditionalInfos.map((info) => {
						return (
							<div className="typography-body-02 view-sample-info-text-row">
								{`${info.additionalFieldName}: `}
								<div className="view-sample-info-info-text">
									{getAdditionalInfoValueDisplayText(info)}
								</div>
							</div>
						);
					})}
					{sampleInstanceAdditionalInfos.map((info) => {
						return (
							<div className="typography-body-02 view-sample-info-text-row">
								{`${info.additionalFieldName}: `}
								<div className="view-sample-info-info-text">
									{getAdditionalInfoValueDisplayText(info)}
								</div>
							</div>
						);
					})}
					{/* <div className="typography-body-02 view-sample-info-text-row">
						{`${translation("Showsampleingallery")}: `}
						<span className="view-sample-info-info-text">
							{sampleBatch.showInGallery
								? translation("Yes")
								: translation("No")}
						</span>
					</div> */}
					{sampleInstance.otherInstructions && (
						<div className="typography-body-02 view-sample-info-text-row">
							{`${translation("OtherInstructions")}: `}
							<span className="view-sample-info-info-text wrap-text">
								{sampleInstance.otherInstructions}
							</span>
						</div>
					)}
				</div>

				<div className="sample-preview-info-column overflow-ellipsis-text sample-preview-expanded-test-info-column">
					<div className="typography-body-02 typography-bold view-sample-info-image-row">
						<div className="sample-information-icon test-tat-icon"></div>
						<div>{translation("TestsAndTAT")}</div>
					</div>

					{testInfoList}
				</div>
			</div>
		</>
	);
	const sampleCard: React.ReactNode = (
		<CollapsibleCard
			className="common-card"
			expanded={expanded}
			content={sampleCardContent}
			collapsibleContent={collapsibleContent}
		></CollapsibleCard>
	);

	return sampleCard;
}
